/*
 * Models for report data.  The gist of this is, every report will (eventually) need to be loaded from
 * the server.  However, the format of the data will vary by report.  This class contains the information
 * needed to load all these various sorts of data.
 */

define([
  "backbone",
  "messaging/messenger",
  "configuration/properties",
  "jquery",
  "modules/reportSplit/reports/singleReport/reportUtilities",
  "modules/learner/learnerModel",
], function (Backbone, Messenger, Properties, $, ReportUtilities, LearnerModel) {
  var DataModel = {};

  var getUrlTemplate = function (urlTemplate) {
    if (urlTemplate) {
      return urlTemplate;
    }
    return Messenger.reqres.request("currentReportUrl");
  };

  /**
   * This is a general-purpose report URL processor.
   * It converts URL from a form such as:
   *    /escalation/{startDate}/{endDate}/{touchpoint
   * to something like:
   *    /escalation/2008-01-01/2009-09-30/phone
   * All report data collections should call this in their URL method.
   * @returns String representing the URL to fetch from
   */
  var reportUrlProcessor = function (urlTemplate) {
    var kbId = Messenger.reqres.request("active:kb");
    var url = Properties.rootURL + "/" + kbId + "/reports";
    url = url + getUrlTemplate(urlTemplate);
    url = ReportUtilities.figureOutText(url);
    return url;
  };

  /**
   * This works like the report URL processor above but is specifically made for
   * setting the offset and batchSize parameters for the topic listing report to
   * their appropriate values as each batch is fetched
   */
  var batchReportUrlProcessor = function (offset, batchSize, urlTemplate) {
    var kbId = Messenger.reqres.request("active:kb");
    var url = Properties.rootURL + "/" + kbId + "/reports";
    url = url + getUrlTemplate(urlTemplate);
    url = ReportUtilities.figureOutText(url, false, offset, batchSize);
    return url;
  };

  DataModel.topicLookup = Backbone.Model.extend({
    initializeCustom: function (options) {
      this.intentName = options.intentName;
    },
    url: function () {
      var kbId = Messenger.reqres.request("active:kb");
      return (
        `${Properties.rootURL}/${kbId}/` +
        `typeahead/intents/match/` +
        `?name=${encodeURIComponent(this.intentName)}`
      );
    },
  });

  /**
   * DataModel.point, simply enough, represents a point on a line chart.
   *
   * Our usual pattern here is that we'll have a Model that's a single data point (like a point), and a
   * corresponding Collection that is a collection of those data points.  Here, point is the model, and
   * lineChartData (after this) is the collection.
   */
  DataModel.point = Backbone.Model.extend({
    defaults: {
      x: 0,
      y: 0,
    },
    /**
     * ValidateAttribute makes sure all attributes of a Model are valid.
     * Here, we are making sure x is unique.  Later on, we might decide duplicate x's are OK here.
     * @param {string} attribute The attribute being checked
     * @param {string} value The value being assigned
     */
    validateAttribute: function (attribute, value) {
      if (attribute === "x") {
        //entry must be unique in the group
        for (var i = 0; i < this.collection.length; i++) {
          var entry = this.collection.at(i);
          if (this !== entry) {
            //don't check the entry against itself
            if (value.toUpperCase() === entry.get("x")) {
              return $.t("report.validationError");
            }
          }
        }
      }
    },
    /**
     * TBH, I'm not sure how many if any of these setters are really necessary.  That's another refactoring task.
     */
    setX: function (newX) {
      this.x = newX;
    },
    /**
     * The id attribute serves as an ID for a model.  You generally want the ID to be unique.
     */
    idAttribute: "x",
  });
  /**
   * A collection of DataModel.point's.
   */
  DataModel.lineChartLine = Backbone.Collection.extend({
    model: DataModel.point,
    /**
     * These url functions are very important.  They are used when fetching data from the web server, and
     * they figure out what URL should be required to request the data.  In particular, they figure out how
     * parameters fit in to the picture.  So, for example, a report will incude a URL when it starts, which will look
     * something like:
     *     /escalationChannel/{touchpoint}
     * The URL method will prepend http://localhost:8087/RD6Authoring/kbs/1031/reports/ (or as appropriate), and will
     * then retrieve the currently-configured parameter for anything in parentheses - something like User%20Generated, resulting
     * in the whole URL of http://localhost:8087/RD6Authoring/kbs/1031/reports/escalationChannel/User%20Generated
     * @returns {String}
     */
    url: function () {
      return reportUrlProcessor(this.urlTemplate);
    },
    /**
     * This uses a numberComparator that I added to our Backbone extensions (at about the same time Jacob did
     * in his branch).
     * @param {type} a
     * @param {type} b
     * @returns the result of the comparison
     */
    comparator: function (a, b) {
      return Backbone.numericComparator(a, b, "x");
    },
    /**
     * Lets us add a new entry to the collection.  This is important.
     * @returns {DataModel.point}
     */
    addNewEntry: function () {
      var newEntry = new DataModel.point();
      this.add(newEntry);
      return newEntry; //return new entry so we can easily manipulate it without searching
    },
  });
  DataModel.lineChartLineTwo = Backbone.Model.extend({
    name: "SomeLine",
    //need a collection
    constructor: function () {
      this.data = new DataModel.point();
      Backbone.Model.apply(this, arguments);
    },
    parse: function (response) {
      console.log("in parse");
      this.set("name", response.name);
      var pts = new DataModel.lineChartLine();
      for (var i = 0; i < response.data.length; i++) {
        pts.add(new DataModel.point(response.data[i]));
      }
      this.set("data", pts);
    },
  });

  DataModel.lineChartLines = Backbone.Collection.extend({
    model: DataModel.lineChartLineTwo,
    url: function () {
      return reportUrlProcessor(this.urlTemplate);
    },
    addNewEntry: function () {
      var newEntry = new DataModel.lineChartLine();
      this.add(newEntry);
      return newEntry;
    },
  });

  DataModel.category = Backbone.Model.extend({
    defaults: {
      value: " ",
    },
    constructor: function () {
      Backbone.Model.apply(this, arguments);
      //this.set('value',arguments[0]);
    },
    //parse does not happen here because this is not directly fetched from the server
    parse: function () {
      console.log("in parse");
    },
  });

  DataModel.categories = Backbone.Collection.extend({
    model: DataModel.category,
  });

  DataModel.lineChart = Backbone.Model.extend({
    constructor: function () {
      this.data = new DataModel.point();
      Backbone.Model.apply(this, arguments);
    },
    parse: function (response) {
      console.log("in parse");
      //this.set('name', response.name);
      var lines = new DataModel.lineChartLines();
      if (response.lines === undefined) {
        response.lines = response;
      }
      for (var i = 0; i < response.lines.length; i++) {
        lines.add(new DataModel.lineChartLineTwo(response.lines[i]));
      }
      this.set("lines", lines);

      var categories = new DataModel.categories();
      if (response.categories !== undefined && response.categories !== null) {
        for (
          var c = 0;
          c < response.categories.length;
          c++ //is a nice programming language.
        ) {
          categories.add(new DataModel.category(response.categories[c]));
        }
      }
      this.set("categories", categories);

      if (response.customTitle) {
        this.set("customTitle", response.customTitle);
      }
      if (response.customXAxis) {
        this.set("customXAxis", response.customXAxis);
      }

      //        var pts = new DataModel.lineChartLine();
      //        for (var i = 0; i < response.data.length; i++)
      //        {
      //          pts.add(new DataModel.point(response.data[i]));
      //        }
      //        this.set('data', pts);
    },
    url: function () {
      return reportUrlProcessor(this.urlTemplate);
    },
  });

  /**
   * A table row can have any type of data in it.
   */
  DataModel.tableRow = Backbone.Model.extend({});

  DataModel.tableData = Backbone.Collection.extend({
    model: DataModel.tableRow,
    /**
     * Same string function.  Yes, it does need to be refactored out.
     * @returns {String}
     */
    url: function () {
      return reportUrlProcessor(this.urlTemplate);
    },
    downloadUrl: function () {
      return reportUrlProcessor(this.downloadUrlTemplate || this.urlTemplate);
    },
    addNewEntry: function () {
      var newEntry = new DataModel.tableRow();
      this.add(newEntry);
      return newEntry; //return new entry so we can easily manipulate it without searching
    },
  });

  Messenger.reqres.setHandler("voc:clustered:search:results", function (urlTemplate) {
    var result = new LearnerModel.clusteredLearnerResults();
    result.urlTemplate = getUrlTemplate(urlTemplate);
    //construct fetch url from the parameter object
    result.url = function () {
      return reportUrlProcessor(this.urlTemplate);
    };
    result.fetch();
    return result;
  });

  /**
   * This begins our second type of data - a slice of a pie chart.
   */
  DataModel.pieSlice = Backbone.Model.extend({
    /**
     * The defaults can be used as a sanity check - if you see the default showing up
     * in HighCharts, then your data isn't getting there properly.
     */
    defaults: {
      name: "Pie Slice",
      y: 25,
    },
    _validateDuplicateName: function (value) {
      for (let i = 0; i < this.collection.length; i++) {
        const entry = this.collection.at(i);
        if (this !== entry) {
          //don't check the entry against itself
          if (value.toUpperCase() === entry.get("name")) {
            return $.t("report.validationError");
          }
        }
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "name") {
        return (
          this.validateRequiredValue(value) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateName(value)
        );
      }
    },
    setValue: function (newValue) {
      this.myValue = newValue;
    },
    idAttribute: "myName",
  });
  /**
   * A collection of pie slices make up a pie.
   */
  DataModel.pieData = Backbone.Collection.extend({
    model: DataModel.pieSlice,
    /**
     * Same string function.  Yes, it does need to be refactored out.
     * @returns {String}
     */
    url: function () {
      return reportUrlProcessor(this.urlTemplate);
    },
    comparator: function (a, b) {
      return Backbone.numericComparator(a, b, "myName");
    },
    addNewEntry: function () {
      var newEntry = new DataModel.pieSlice();
      this.add(newEntry);
      return newEntry; //return new entry so we can easily manipulate it without searching
    },
  });
  /**
   * This isn't fully fleshed out since I ended up using pie chart data for bar/column charts.
   * That doesn't mean it shouldn't be fleshed out, though.
   */
  DataModel.barData = Backbone.Model.extend({
    defaults: {
      category: [],
      values: [],
    },
    url: function () {
      return reportUrlProcessor(this.urlTemplate);
    },
  });
  /**
   * Data for scatter plots.  The only different from DataModel.point is that you can have multiple points
   * with the same x, so it's a fair question if it should really be distinct.
   */
  DataModel.scatterPoint = Backbone.Model.extend({
    defaults: {
      x: 100000,
      y: 250000,
    },
    //everything is valid, you can have multiple points at the same location
    setValue: function (newX) {
      this.x = newX;
    },
    idAttribute: "x",
  });
  /**
   * Collection of scatter points.  I believe this isn't actually being used right now.
   */
  DataModel.scatterPoints = Backbone.Collection.extend({
    model: DataModel.scatterPoint,
    //comparator?
    addNewEntry: function () {
      var newEntry = new DataModel.scatterPoint();
      this.add(newEntry);
      return newEntry; //return new entry so we can easily manipulate it without searching
    },
  });
  /**
   * A single series (singular: serie?) for a scatter plot.  These are seen in the "Scatter Plot" graph.
   */
  DataModel.serie = Backbone.Model.extend({
    defaults: {
      name: "Atlantis",
    },
    initialize: function () {
      this.data = new DataModel.scatterPoint();
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "name") {
        return this.validateRequiredValue(value) || this.validateSafeCharacters(value);
      }
    },
    setData: function (newX) {
      this.data = newX;
    },
    idAttribute: "name",
  });
  //todo: figure out how this should *really* be structured
  /**
   * This what we retrieve from the Module for the scatter plot.  It's a collection of serie's, each of which has a Model.
   * This is NOT necessarily an ideal series construction, because on the chart we get a series per point.  The chart itself
   * displays nice, but the legend is less than ideal.
   */
  DataModel.scatterSeries = Backbone.Collection.extend({
    model: DataModel.serie,
    url: function () {
      return reportUrlProcessor(this.urlTemplate);
    },
    comparator: function (a, b) {
      return Backbone.numericComparator(a, b, "myName");
    },
    addNewEntry: function () {
      var newEntry = new DataModel.serie();
      this.add(newEntry);
      return newEntry; //return new entry so we can easily manipulate it without searching
    },
  });
  /**
   * This begins our second type of data - a slice of a pie chart.
   */
  DataModel.bubble = Backbone.Model.extend({
    /**
     * The defaults can be used as a sanity check - if you see the default showing up
     * in HighCharts, then your data isn't getting there properly.
     */
    defaults: {
      name: "Bubble",
    },
    _validateDuplicateName: function (value) {
      for (let i = 0; i < this.collection.length; i++) {
        const entry = this.collection.at(i);
        if (this !== entry) {
          //don't check the entry against itself
          if (value.toUpperCase() === entry.get("name")) {
            return $.t("report.validationError");
          }
        }
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "name") {
        return (
          this.validateRequiredValue(value) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateName(value)
        );
      }
    },
    setValue: function (newX) {
      this.name = newX;
    },
    idAttribute: "name",
  });
  /**
   * A collection of pie slices make up a pie.
   */
  DataModel.bubbleCollection = Backbone.Collection.extend({
    model: DataModel.bubble,
    /**
     * Same string function.  Yes, it does need to be refactored out.
     * @returns {String}
     */
    url: function () {
      return reportUrlProcessor(this.urlTemplate);
    },
    addNewEntry: function () {
      var newEntry = new DataModel.bubble();
      this.add(newEntry);
      return newEntry; //return new entry so we can easily manipulate it without searching
    },
  });
  /**
   * HEREAFTER ARE REQRES HANDLERS.
   * Above are the data models.  Below is where we actually retrieve the data from the web server.
   */

  /**
   * Custom loader for the Population of Japan graph.  We should generalize this for line charts.
   */
  Messenger.reqres.setHandler("lineChartData", function (urlTemplate) {
    console.log("get reps");
    var reps = new DataModel.lineChartLine();
    reps.urlTemplate = getUrlTemplate(urlTemplate);
    reps.on("sync", function () {
      this.sort();
    });
    //fetch from the server
    reps.fetch({
      success: function (response, xhr) {
        console.log("Success!");
        console.log(response);
        //reps = xhr;
        return xhr;
      },
      error: function (response, xhr) {
        console.log(response);
        Messenger.okDialog(
          "Error while loading line data.  Error code: " +
            xhr.status +
            ": " +
            xhr.statusText +
            ".  URL attempted:\n" +
            reportUrlProcessor(reps.urlTemplate),
        );
      },
    });
    return reps;
  });
  Messenger.reqres.setHandler("multiLineData", function (urlTemplate) {
    console.log("get multi-line");
    var reps = new DataModel.lineChart();
    reps.urlTemplate = getUrlTemplate(urlTemplate);
    //fetch from the server
    reps.fetch({
      success: function (response, xhr) {
        console.log("Success!");
        console.log(response);
        return xhr;
      },
      error: function (response, xhr) {
        console.log(response);
        Messenger.okDialog(
          "Error while loading line data.  Error code: " +
            xhr.status +
            ": " +
            xhr.statusText +
            ".  URL attempted:\n" +
            reportUrlProcessor(reps.urlTemplate),
        );
      },
    });
    return reps;
  });

  /**
   * Handler for bar charts
   */
  Messenger.reqres.setHandler("barChartData", function (urlTemplate) {
    console.log("get bar chart");
    var reps = new DataModel.barChart();
    reps.urlTemplate = getUrlTemplate(urlTemplate);
    //fetch from the server
    reps.fetch({
      success: function (response, xhr) {
        console.log("Success!");
        console.log(response);
        return xhr;
      },
      error: function (response, xhr) {
        console.log(response);
        Messenger.okDialog(
          "Error while loading line data.  Error code: " +
            xhr.status +
            ": " +
            xhr.statusText +
            ".  URL attempted:\n" +
            reportUrlProcessor(reps.urlTemplate),
        );
      },
    });
    return reps;
  });

  DataModel.barChart = Backbone.Model.extend({
    url: function () {
      return reportUrlProcessor(this.urlTemplate);
    },
  });

  /**
   * Handler for pie charts.
   */
  Messenger.reqres.setHandler("pieData", function (urlTemplate) {
    console.log("get reps");
    var reps = new DataModel.pieData();
    reps.urlTemplate = getUrlTemplate(urlTemplate);
    reps.on("sync", function () {
      this.sort();
    });
    //fetch from the server
    reps.fetch({
      success: function (response, xhr) {
        console.log("Success!");
        console.log(response);
        //reps = xhr;
        return xhr;
      },
      error: function (response, xhr) {
        console.log(JSON.stringify(xhr));
        Messenger.okDialog(
          "Error while loading column or pie data.  Error code: " +
            xhr.status +
            ": " +
            xhr.statusText +
            ".  URL attempted:\n" +
            reportUrlProcessor(reps.urlTemplate),
        );
      },
    });
    return reps;
  });
  /**
   * Handler for tables
   */
  Messenger.reqres.setHandler("tableData", function (urlTemplate) {
    console.log("get reps");
    var reps = new DataModel.tableData();
    reps.urlTemplate = getUrlTemplate(urlTemplate);
    //fetch from the server
    reps.fetch({
      success: function (response, xhr) {
        console.log("Success!");
        console.log(response);
        //reps = xhr;
        return xhr;
      },
      error: function (response, xhr) {
        console.log(JSON.stringify(xhr));
        Messenger.okDialog(
          "Error while loading column or pie data.  Error code: " +
            xhr.status +
            ": " +
            xhr.statusText +
            ".  URL attempted:\n" +
            reportUrlProcessor(reps.urlTemplate),
        );
      },
    });
    return reps;
  });
  /**
   * Handler for Scatter Plot.  We should generalize this for scatter plots, though perhaps after deciding upon a preferred format.
   */
  Messenger.reqres.setHandler("countryData", function (urlTemplate) {
    console.log("get reps");
    var reps = new DataModel.scatterSeries();
    reps.urlTemplate = getUrlTemplate(urlTemplate);
    reps.on("sync", function () {
      this.sort();
    });
    //fetch from the server
    reps.fetch({
      success: function (response, xhr) {
        console.log("Success!");
        console.log(response);
        //reps = xhr;
        return xhr;
      },
      error: function (response) {
        console.log(response);
      },
    });
    return reps;
  });
  /**
   * Handler for bubble data
   */
  Messenger.reqres.setHandler("bubbleData", function (urlTemplate) {
    console.log("get reps");
    var reps = new DataModel.bubbleCollection();
    reps.urlTemplate = getUrlTemplate(urlTemplate);
    reps.on("sync", function () {
      //do not sort
    });
    //fetch from the server
    reps.fetch({
      success: function (response, xhr) {
        console.log("Success!");
        console.log(response);
        //reps = xhr;
        return xhr;
      },
      error: function (response, xhr) {
        console.log(JSON.stringify(xhr));
      },
    });
    return reps;
  });

  /**
   * Special handler for fetching the topic listing data in batches
   */
  Messenger.reqres.setHandler("topic:listing:batch:data", function (urlTemplate) {
    var offset = 0;
    var percentComplete = 0;
    var batchSize;
    var topicCount = Messenger.reqres.request("fetch:topic:listing:count");

    //currently, the total topics are divided into 10 batches with the final batch fetching the remaining topics
    //if there are less than 10 topics, then it only fetches one batch
    if (topicCount < 10) {
      batchSize = topicCount;
    } else {
      batchSize = Math.floor(topicCount / 10);
    }

    var reps = new DataModel.tableData();
    reps.urlTemplate = getUrlTemplate(urlTemplate);
    reps.downloadUrlTemplate = reps.urlTemplate
      .replace("/{offset}", "")
      .replace("/{batchSize}", "");

    var batchSuccess = function (response, xhr) {
      console.log("Success!");
      percentComplete += 10;
      Messenger.vent.trigger("update:progress:bar", percentComplete);
      var remaining = topicCount - (offset + batchSize);
      if (remaining > 0) {
        offset = offset + batchSize;
        //if this the the final batch, grab all the remaining topics
        if (offset === batchSize * 9) {
          batchSize = topicCount - offset;
        }
        //recursively fetch the next batch with the new offset and batch size adjusted in the URL
        reps.fetch({
          url: batchReportUrlProcessor(offset, batchSize, reps.urlTemplate),
          success: batchSuccess,
          failure: batchFailure,
        });
      } else {
        Messenger.vent.trigger("topic:listing:batch:complete");
        return xhr;
      }
    };

    var batchFailure = function (response, xhr) {
      console.log(JSON.stringify(xhr));
      Messenger.okDialog(
        "Error while loading topic listing batch data.  Error code: " +
          xhr.status +
          ": " +
          xhr.statusText +
          ".  URL attempted:\n" +
          reportUrlProcessor(reps.urlTemplate),
      );
    };
    reps.fetch({
      url: batchReportUrlProcessor(offset, batchSize, reps.urlTemplate),
      success: batchSuccess,
      failure: batchFailure,
    });
    return reps;
  });

  /**
   * A single number can have a single value in it.
   */
  DataModel.singleNumber = Backbone.Model.extend({
    url: function () {
      return reportUrlProcessor(this.urlTemplate);
    },
  });
  /**
   * Handler for single number data
   */
  Messenger.reqres.setHandler("singleNumberData", function (urlTemplate) {
    var data = new DataModel.singleNumber();
    data.urlTemplate = getUrlTemplate(urlTemplate);
    //fetch from the server
    data.fetch({
      success: function (response, xhr) {
        console.log("Success!");
        console.log(response);
        return xhr;
      },
      error: function (response, xhr) {
        console.log(JSON.stringify(xhr));
      },
    });
    return data;
  });

  return DataModel;
});
